import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { MdEdit, MdRefresh, MdSend } from 'react-icons/md';
import { CONFIG } from 'variables/config';
import SearchSelect from 'components/fields/SearchSelect';
import { productOptions } from 'views/backoffice/productMismatches/ProductPicker';

// Colonne « Transfert Batiweb » de l'onglet No match : ce qui est arrivé au lead chez Batiweb /
// HelloArtisan, ou pourquoi il n'y a jamais été envoyé. Calculé côté back (describeBatiwebForwarding).

export type BatiwebForwardState =
    | 'sending'
    | 'not_sellable'
    | 'pending'
    | 'sold'
    | 'unsold'
    | 'error'
    | 'hidden'
    | 'no_subindustry'
    | 'unknown_subindustry'
    | 'to_configure'
    | 'no_equivalent'
    | 'not_sent';

export interface BatiwebForward {
    state: BatiwebForwardState;
    industry?: string | null;
    subIndustry?: string | null;
    worktypeLabel?: string | null;
    detail?: string | null;
    importedAt?: string | null;
    expectedResponseAt?: string | null;
    lastCheckedAt?: string | null;
    resolvedAt?: string | null;
    hiddenAt?: string | null;
    overdue?: boolean;
    // not_sellable / error : raison lisible du refus.
    reason?: string | null;
    // Dernier « Retester » (is_sellable relancé depuis cette page).
    sellableCheckedAt?: string | null;
    sellableCheckedBy?: string | null;
    sellableNow?: boolean | null;
}

// Pastilles flat : fond teinté très léger, sans bordure, texte et point de la même teinte.
// Palette sur mesure (pas les couleurs Tailwind par défaut), déclinée en clair et en sombre.
export const PILL_TONES = {
    green: { pill: 'bg-[#E7F7F0] text-[#0B7A57] dark:bg-[#12B886]/15 dark:text-[#5FDBB0]', dot: 'bg-[#12B886]' },
    blue: { pill: 'bg-[#EDEFFE] text-[#3E46C9] dark:bg-[#6170F5]/15 dark:text-[#A5ADFF]', dot: 'bg-[#6170F5]' },
    amber: { pill: 'bg-[#FDF3E3] text-[#9A5B00] dark:bg-[#F2A93B]/15 dark:text-[#F6C77D]', dot: 'bg-[#F2A93B]' },
    red: { pill: 'bg-[#FDEDEE] text-[#C2323F] dark:bg-[#F0505F]/15 dark:text-[#FF9AA4]', dot: 'bg-[#F0505F]' },
    gray: { pill: 'bg-[#F1F3F6] text-[#596275] dark:bg-white/[0.06] dark:text-[#AEB6C4]', dot: 'bg-[#97A0B0]' },
};
export type PillTone = keyof typeof PILL_TONES;

// Pastille de statut partagée par les colonnes Batiweb et Marketplace.
// hollow = point creux (jamais envoyé / non visible), live = point animé (réponse attendue).
export const StatusPill = ({ tone, label, hollow, live }: { tone: PillTone; label: string; hollow?: boolean; live?: boolean }) => (
    <span className={`inline-flex w-fit cursor-default items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-[3px] text-[11px] font-medium leading-4 tracking-[0.01em] ${PILL_TONES[tone].pill}`}>
        <span className="relative flex h-1.5 w-1.5 shrink-0">
            {live && <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${PILL_TONES[tone].dot}`} />}
            <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${hollow ? 'bg-transparent ring-[1.5px] ring-inset ring-current' : PILL_TONES[tone].dot}`} />
        </span>
        {label}
    </span>
);

// Bulle d'explication au survol, partagée par les colonnes Batiweb et Marketplace. Rendue dans un
// portail en position fixe : le conteneur overflow-x-auto du tableau la couperait sinon.
const TOOLTIP_WIDTH = 300;
export const HoverExplanation = ({ title, children, trigger }: { title: string; children: React.ReactNode; trigger: JSX.Element }) => {
    const ref = useRef<HTMLSpanElement>(null);
    const [position, setPosition] = useState<{ left: number; top: number; below: boolean } | null>(null);

    const show = () => {
        const rect = ref.current?.getBoundingClientRect();
        if (!rect) return;
        const below = rect.top < 180;
        setPosition({
            left: Math.max(8, Math.min(rect.left, window.innerWidth - TOOLTIP_WIDTH - 8)),
            top: below ? rect.bottom + 6 : rect.top - 6,
            below,
        });
    };

    return (
        <span ref={ref} onMouseEnter={show} onMouseLeave={() => setPosition(null)} className="inline-flex">
            {trigger}
            {position &&
                createPortal(
                    <div
                        role="tooltip"
                        style={{ position: 'fixed', left: position.left, top: position.top, width: TOOLTIP_WIDTH, transform: position.below ? undefined : 'translateY(-100%)' }}
                        className="pointer-events-none z-[1000] rounded-lg border border-[#E3E6EC] bg-white px-3 py-2.5 text-xs leading-relaxed shadow-[0_8px_24px_rgba(16,24,40,0.12)] dark:border-white/10 dark:bg-navy-800"
                    >
                        <div className="mb-1 font-semibold text-gray-900 dark:text-white">{title}</div>
                        <div className="text-gray-600 dark:text-gray-300">{children}</div>
                    </div>,
                    document.body
                )}
        </span>
    );
};

// meaning = ce que le statut veut dire pour Batiweb ; action = quoi faire, le cas échéant.
export const STATES: Record<BatiwebForwardState, { label: string; tone: PillTone; meaning: string; action?: string }> = {
    sold: { label: 'Vendu', tone: 'green', meaning: 'Batiweb a validé et acheté ce lead. Il passe en « Approuvés » au prochain passage du cron (9h), vendeur à payer.' },
    pending: { label: 'En attente', tone: 'blue', meaning: "Le lead a été importé chez Batiweb, qui ne s'est pas encore prononcé. Le statut est revérifié chaque jour à 9h." },
    sending: { label: 'Envoi en cours', tone: 'blue', meaning: "L'envoi à Batiweb est en cours, ou une erreur réseau empêche de savoir s'il a été reçu. Le cron le revérifie automatiquement." },
    unsold: { label: 'Refusé', tone: 'red', meaning: "Batiweb a reçu le lead puis l'a annulé. Il est revenu sur la Bourse du Lead." },
    not_sellable: {
        label: 'Refusé par Batiweb',
        tone: 'red',
        meaning: "Batiweb n'a pas accepté ce lead (vérification is_sellable), ou il manquait des champs requis : il n'a jamais été importé et reste sur la Bourse du Lead.",
        action: 'Corrigez les réponses du lead si besoin, puis « Retester ».',
    },
    error: { label: 'Erreur', tone: 'red', meaning: "Erreur de l'API Batiweb au moment de l'envoi : le lead n'a jamais été importé.", action: '« Retester » pour relancer la vérification.' },
    hidden: { label: 'Masqué', tone: 'gray', meaning: 'Un admin a masqué ce lead sur la page Batiweb : il ne sera jamais envoyé à Batiweb.' },
    no_subindustry: {
        label: 'Sans produit',
        tone: 'amber',
        meaning:
            "On ne sait pas quel produit concerne ce lead : ni le formulaire ni la page d'origine ne l'indiquent. Or Batiweb classe ses leads par métier, choisi à partir du produit — sans produit, impossible de choisir un métier, donc le lead n'est jamais envoyé à Batiweb.",
        action: "« Attribuer un produit » au lead, puis l'envoyer. Pour les prochains leads : définir le produit du formulaire ou de la page d'origine.",
    },
    unknown_subindustry: {
        label: 'Produit introuvable',
        tone: 'amber',
        meaning:
            "Le lead a bien un produit, mais ce produit n'existe plus dans la liste des produits (renommé ou supprimé depuis). Impossible de retrouver le métier Batiweb associé, donc le lead n'est jamais envoyé à Batiweb.",
        action: "« Corriger le produit » pour lui réattribuer un produit existant (seul le lead est modifié), puis l'envoyer.",
    },
    to_configure: {
        label: 'Métier à paramétrer',
        tone: 'amber',
        meaning: "Ce produit n'a pas encore de métier Batiweb associé : le lead n'a pas pu être envoyé.",
        action: 'Associer un métier dans Batiweb › Matching produits.',
    },
    no_equivalent: { label: "Pas d'équivalent", tone: 'gray', meaning: "Ce produit est marqué « pas d'équivalent » chez Batiweb : ses leads ne leur sont jamais envoyés." },
    not_sent: {
        label: 'Pas encore envoyé',
        tone: 'gray',
        meaning: "Le produit a un métier Batiweb, mais le lead ne leur a jamais été envoyé (envoi automatique désactivé, ou lead antérieur à l'intégration).",
        action: '« Envoyer à Batiweb ».',
    },
};

const NEVER_SENT_STATES: BatiwebForwardState[] = ['hidden', 'no_subindustry', 'unknown_subindustry', 'to_configure', 'no_equivalent', 'not_sent'];
const RECHECKABLE_STATES: BatiwebForwardState[] = ['not_sellable', 'error'];
// Produit du lead absent ou disparu du catalogue : réattribuable depuis la cellule.
const PRODUCT_FIXABLE_STATES: BatiwebForwardState[] = ['no_subindustry', 'unknown_subindustry'];

export type CatalogIndustry = { name: string; subIndustries: { name: string }[] };

const normalize = (value: string) =>
    value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

// Pré-sélection : le produit du catalogue au nom le plus proche de l'ancien (même nom à la casse /
// aux accents près, ou l'un contenu dans l'autre), dans la même industrie de préférence.
export function suggestProduct(industries: CatalogIndustry[], industry?: string | null, subIndustry?: string | null): string {
    if (!subIndustry) return '';
    const target = normalize(subIndustry);
    const ordered = [...industries].sort((a, b) => Number(b.name === industry) - Number(a.name === industry));
    for (const exact of [true, false]) {
        for (const ind of ordered) {
            const sub = ind.subIndustries.find((s) => {
                const name = normalize(s.name);
                return exact ? name === target : !!name && !!target && (name.includes(target) || target.includes(name));
            });
            if (sub) return `${ind.name}|${sub.name}`;
        }
    }
    return '';
}

const shortDate = (value?: string | null) =>
    value ? new Date(value).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : null;
const shortDateTime = (value?: string | null) =>
    value ? new Date(value).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : null;

const Spinner = () => <span className="h-3 w-3 animate-spin rounded-full border-[1.5px] border-current border-t-transparent" />;

const BatiwebForwardCell = ({
    leadId,
    forward,
    industries,
    onPatch,
    onSent,
}: {
    leadId: string;
    forward?: BatiwebForward | null;
    // Catalogue des produits, pour « Corriger le produit ».
    industries: CatalogIndustry[];
    // Met à jour la ligne localement après « Retester » (sans recharger tout le tableau).
    onPatch: (patch: Partial<BatiwebForward>) => void;
    // Après un envoi à Batiweb : statut et visibilité Marketplace changent, on recharge.
    onSent: () => void;
}) => {
    const [busy, setBusy] = useState<'recheck' | 'send' | 'product' | null>(null);
    const [error, setError] = useState<string | null>(null);
    // Valeur « industrie|produit » du sélecteur ; null = sélecteur fermé.
    const [productChoice, setProductChoice] = useState<string | null>(null);

    if (!forward) return <span className="text-gray-300">—</span>;
    const config = STATES[forward.state] || { label: forward.state, tone: 'gray' as const, meaning: '' };
    const product = forward.subIndustry || null;

    const lines: string[] = [];
    if (forward.worktypeLabel) lines.push(`Métier : ${forward.worktypeLabel}`);
    if (forward.state === 'unknown_subindustry' && product) lines.push(`Produit : ${product}`);
    if (forward.importedAt) lines.push(`Envoyé le ${shortDate(forward.importedAt)}`);
    if (forward.state === 'pending' && forward.expectedResponseAt) lines.push(`Réponse attendue le ${shortDate(forward.expectedResponseAt)}`);
    if (forward.resolvedAt) lines.push(`${forward.state === 'sold' ? 'Vendu' : 'Refusé'} le ${shortDate(forward.resolvedAt)}`);
    if (forward.hiddenAt) lines.push(`Le ${shortDate(forward.hiddenAt)}${forward.detail ? ` par ${forward.detail}` : ''}`);

    // Refus : la raison lisible prime ; sinon message brut de Batiweb / statusLabel.
    const isRefused = RECHECKABLE_STATES.includes(forward.state);
    const reason = isRefused ? forward.reason || forward.detail : forward.state !== 'hidden' ? forward.detail : null;

    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

    const handleRecheck = async () => {
        setBusy('recheck');
        setError(null);
        try {
            const res = await axios.post(`${CONFIG.apiUrl}/admin/batiweb/leads/${leadId}/recheck-sellable`, {}, { headers });
            const checkedAt = new Date().toISOString();
            onPatch(
                res.data.sellable
                    ? { sellableNow: true, sellableCheckedAt: checkedAt, sellableCheckedBy: null }
                    : { state: 'not_sellable', sellableNow: false, sellableCheckedAt: checkedAt, sellableCheckedBy: null, reason: res.data.message }
            );
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Échec du test is_sellable');
        } finally {
            setBusy(null);
        }
    };

    const handleSaveProduct = async () => {
        if (!productChoice) return;
        const [industry, subIndustry] = productChoice.split('|');
        setBusy('product');
        setError(null);
        try {
            const res = await axios.put(`${CONFIG.apiUrl}/admin/batiweb/leads/${leadId}/product`, { industry, subIndustry }, { headers });
            onPatch(res.data.forward);
            setProductChoice(null);
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Échec de la modification du produit');
        } finally {
            setBusy(null);
        }
    };

    const handleSend = async () => {
        if (!window.confirm("Envoyer ce lead à Batiweb ?\n\nS'il est vendable, il sera importé chez eux et retiré de la Bourse du Lead.")) return;
        setBusy('send');
        setError(null);
        try {
            await axios.post(`${CONFIG.apiUrl}/admin/batiweb/leads/${leadId}/send`, {}, { headers });
            onSent();
        } catch (e: any) {
            setError(e?.response?.data?.message || "Échec de l'envoi à Batiweb");
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="flex max-w-[240px] flex-col gap-1">
            <div className="flex flex-wrap items-center gap-1">
                <HoverExplanation
                    title={`Batiweb — ${config.label}`}
                    trigger={
                        <span>
                            <StatusPill
                                tone={config.tone}
                                label={config.label}
                                hollow={NEVER_SENT_STATES.includes(forward.state)}
                                live={forward.state === 'pending' || forward.state === 'sending'}
                            />
                        </span>
                    }
                >
                    <p>{config.meaning}</p>
                    {product && forward.state !== 'unknown_subindustry' && <p className="mt-1">Produit : {product}</p>}
                    {config.action && <p className="mt-1 font-medium text-gray-800 dark:text-gray-100">→ {config.action}</p>}
                    {forward.lastCheckedAt && <p className="mt-1 text-gray-400">Dernière vérification : {shortDateTime(forward.lastCheckedAt)}</p>}
                    {forward.state === 'not_sellable' && reason && <p className="mt-1 text-gray-400">{reason}</p>}
                    {isRefused && forward.detail && forward.detail !== reason && <p className="mt-1 text-gray-400">Message Batiweb : {forward.detail}</p>}
                </HoverExplanation>
                {forward.overdue && <StatusPill tone="amber" label="En retard" />}
            </div>

            {/* Non vendable : le tag suffit, la raison détaillée reste dans l'infobulle. */}
            {reason && forward.state !== 'not_sellable' && (
                <span className={`pl-0.5 text-[11px] leading-4 ${isRefused ? 'font-medium text-[#C2323F] dark:text-[#FF9AA4]' : 'text-[#596275] dark:text-[#AEB6C4]'}`}>
                    {reason}
                </span>
            )}
            {lines.map((line) => (
                <span key={line} className="pl-0.5 text-[10px] leading-4 text-[#8A93A3] dark:text-[#7E8798]">{line}</span>
            ))}

            {isRefused && (
                <div className="mt-0.5 flex flex-wrap items-center gap-1">
                    {forward.sellableNow && <StatusPill tone="green" label="Vendable au dernier test" />}
                    <button
                        type="button"
                        onClick={handleRecheck}
                        disabled={!!busy}
                        title="Relancer is_sellable chez Batiweb et enregistrer le résultat (n'envoie rien)"
                        className="inline-flex h-6 items-center gap-1 rounded-md border border-[#E3E6EC] px-1.5 text-[11px] font-medium text-[#596275] transition hover:border-[#C9CED8] hover:bg-[#F6F7F9] disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:text-[#AEB6C4] dark:hover:bg-white/5"
                    >
                        {busy === 'recheck' ? <Spinner /> : <MdRefresh size={13} />}
                        {busy === 'recheck' ? 'Test en cours…' : 'Retester'}
                    </button>
                    {forward.sellableNow && (
                        <button
                            type="button"
                            onClick={handleSend}
                            disabled={!!busy}
                            className="inline-flex h-6 items-center gap-1 rounded-md bg-[#12B886] px-1.5 text-[11px] font-medium text-white transition hover:bg-[#0FA377] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {busy === 'send' ? <Spinner /> : <MdSend size={12} />}
                            Envoyer à Batiweb
                        </button>
                    )}
                </div>
            )}
            {PRODUCT_FIXABLE_STATES.includes(forward.state) &&
                (productChoice === null ? (
                    <button
                        type="button"
                        onClick={() => setProductChoice(suggestProduct(industries, forward.industry, forward.subIndustry))}
                        disabled={!!busy || industries.length === 0}
                        title="Réattribuer à ce lead un produit du catalogue (ni l'offre, ni le template, ni le catalogue ne sont modifiés)"
                        className="mt-0.5 inline-flex h-6 w-fit items-center gap-1 rounded-md border border-[#E3E6EC] px-1.5 text-[11px] font-medium text-[#596275] transition hover:border-[#C9CED8] hover:bg-[#F6F7F9] disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:text-[#AEB6C4] dark:hover:bg-white/5"
                    >
                        <MdEdit size={12} />
                        {forward.state === 'unknown_subindustry' ? 'Corriger le produit' : 'Attribuer un produit'}
                    </button>
                ) : (
                    <div className="mt-0.5 flex flex-col gap-1">
                        <SearchSelect
                            options={productOptions(industries)}
                            value={productChoice}
                            onChange={setProductChoice}
                            disabled={busy === 'product'}
                            placeholder="Choisir un produit…"
                            searchPlaceholder="Rechercher un produit ou une industrie"
                            className="w-full"
                        />
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={handleSaveProduct}
                                disabled={!productChoice || !!busy}
                                className="inline-flex h-6 items-center gap-1 rounded-md bg-[#6170F5] px-1.5 text-[11px] font-medium text-white transition hover:bg-[#4F5DE0] disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {busy === 'product' && <Spinner />}
                                Enregistrer
                            </button>
                            <button
                                type="button"
                                onClick={() => setProductChoice(null)}
                                disabled={busy === 'product'}
                                className="inline-flex h-6 items-center rounded-md px-1.5 text-[11px] font-medium text-[#596275] transition hover:bg-[#F6F7F9] dark:text-[#AEB6C4] dark:hover:bg-white/5"
                            >
                                Annuler
                            </button>
                        </div>
                    </div>
                ))}
            {forward.state === 'not_sent' && (
                <button
                    type="button"
                    onClick={handleSend}
                    disabled={!!busy}
                    className="mt-0.5 inline-flex h-6 w-fit items-center gap-1 rounded-md bg-[#12B886] px-1.5 text-[11px] font-medium text-white transition hover:bg-[#0FA377] disabled:cursor-not-allowed disabled:opacity-60"
                >
                    {busy === 'send' ? <Spinner /> : <MdSend size={12} />}
                    Envoyer à Batiweb
                </button>
            )}
            {isRefused && forward.sellableCheckedAt && (
                <span className="pl-0.5 text-[10px] leading-4 text-[#8A93A3] dark:text-[#7E8798]">
                    Testé le {shortDateTime(forward.sellableCheckedAt)}
                    {forward.sellableCheckedBy ? ` par ${forward.sellableCheckedBy}` : ''}
                </span>
            )}
            {error && <span className="pl-0.5 text-[10px] leading-4 text-[#C2323F] dark:text-[#FF9AA4]">{error}</span>}
        </div>
    );
};

export default BatiwebForwardCell;
